import { useState, useEffect, useCallback } from "react";
import Swipeable from "@/components/Swipeable";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { 
  ArrowLeft,
  Upload,
  Grid3X3,
  Calendar,
  Building2,
  FileText,
  Trash2,
  CheckCircle2,
  Loader2,
  RefreshCw,
  X,
  ChevronDown,
  IndianRupee,
  Users,
  MapPin,
  Percent,
  ArrowRight,
  Menu
} from "lucide-react";
import { authGet, authPost, authDelete, getAuthToken } from "../utils/auth";
import MobileBottomNav from "../components/MobileBottomNav";
import MobileSidebar from "../components/MobileSidebar";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

export default function HealthGrid() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [entries, setEntries] = useState([]);
  const [filters, setFilters] = useState({
    available_months: [],
    companies: [],
    plans: [],
    sum_insureds: [],
    family_compositions: [],
    locations: []
  });
  
  // Cascading filter states
  const [selectedMonth, setSelectedMonth] = useState("");
  const [selectedYear, setSelectedYear] = useState("");
  const [selectedCompany, setSelectedCompany] = useState("");
  const [selectedPlan, setSelectedPlan] = useState("");
  const [selectedSumInsured, setSelectedSumInsured] = useState("");
  const [selectedFamily, setSelectedFamily] = useState("");
  const [selectedLocation, setSelectedLocation] = useState("");
  
  // Upload modal states
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadMonth, setUploadMonth] = useState(new Date().getMonth() + 1);
  const [uploadYear, setUploadYear] = useState(new Date().getFullYear());
  const [analyzedEntries, setAnalyzedEntries] = useState([]);
  const [uploadFile, setUploadFile] = useState(null);
  const [showSidebar, setShowSidebar] = useState(false);

  useEffect(() => {
    const token = getAuthToken();
    if (!token) {
      navigate("/advisor/login");
      return;
    }
    fetchGridData();
  }, [navigate]);

  const fetchGridData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Build query params for cascading filters
      const params = new URLSearchParams();
      if (selectedMonth) params.append("month", selectedMonth);
      if (selectedYear) params.append("year", selectedYear);
      if (selectedCompany) params.append("company", selectedCompany);
      if (selectedPlan) params.append("plan", selectedPlan);
      if (selectedSumInsured) params.append("sum_insured", selectedSumInsured);
      if (selectedFamily) params.append("family_composition", selectedFamily);
      if (selectedLocation) params.append("location", selectedLocation);
      
      const queryString = params.toString();
      const url = `/api/advisor/health-grids${queryString ? `?${queryString}` : ''}`;
      
      const res = await authGet(url);
      
      if (res.ok) {
        setEntries(res.data.entries || []);
        setFilters(res.data.filters || {
          available_months: [],
          companies: [],
          plans: [],
          sum_insureds: [],
          family_compositions: [],
          locations: []
        });
      }
    } catch (error) {
      console.error("Error fetching grid data:", error);
    } finally {
      setLoading(false);
    }
  }, [selectedMonth, selectedYear, selectedCompany, selectedPlan, selectedSumInsured, selectedFamily, selectedLocation]);

  useEffect(() => {
    const token = getAuthToken();
    if (token) {
      fetchGridData();
    }
  }, [fetchGridData]);

  // Reset dependent filters when parent changes
  const handleCompanyChange = (value) => {
    setSelectedCompany(value);
    setSelectedPlan("");
    setSelectedSumInsured("");
    setSelectedFamily("");
    setSelectedLocation("");
  };

  const handlePlanChange = (value) => {
    setSelectedPlan(value);
    setSelectedSumInsured("");
    setSelectedFamily("");
    setSelectedLocation("");
  };

  const handleSumInsuredChange = (value) => {
    setSelectedSumInsured(value);
    setSelectedFamily("");
    setSelectedLocation("");
  };

  const handleFamilyChange = (value) => {
    setSelectedFamily(value);
    setSelectedLocation("");
  };

  const handleMonthYearChange = (monthYear) => {
    if (monthYear) {
      const [m, y] = monthYear.split("-");
      setSelectedMonth(m);
      setSelectedYear(y);
    } else {
      setSelectedMonth("");
      setSelectedYear("");
    }
    // Reset all other filters
    setSelectedCompany("");
    setSelectedPlan("");
    setSelectedSumInsured("");
    setSelectedFamily("");
    setSelectedLocation("");
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setUploadFile(file);
      setAnalyzedEntries([]);
    }
  };

  const analyzePdf = async () => {
    if (!uploadFile) return;
    
    setAnalyzing(true);
    try {
      const formData = new FormData();
      formData.append("file", uploadFile);
      formData.append("month", uploadMonth.toString());
      formData.append("year", uploadYear.toString());
      
      const token = getAuthToken();
      const response = await fetch(`${API_URL}/api/advisor/health-grids/analyze-pdf`, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${token}`
        },
        body: formData
      });
      
      // Read response as text first to handle non-JSON responses
      const responseText = await response.text();
      
      let data;
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        console.error("Failed to parse response:", responseText);
        throw new Error("Server returned invalid response. Please try again.");
      }
      
      if (!response.ok) {
        if (response.status === 402) {
          throw new Error("AI analysis budget exceeded. Please add balance to your Universal Key.");
        }
        throw new Error(data.detail || "Failed to analyze PDF");
      }
      
      if (!data.entries || data.entries.length === 0) {
        throw new Error("No data could be extracted from the PDF. Please try a clearer document.");
      }
      
      setAnalyzedEntries(data.entries);
    } catch (error) {
      console.error("Error analyzing PDF:", error);
      alert(`Error: ${error.message}`);
    } finally {
      setAnalyzing(false);
    }
  };

  const saveEntries = async () => {
    if (analyzedEntries.length === 0) return;
    
    setUploading(true);
    try {
      const res = await authPost("/api/advisor/health-grids/upload", {
        month: uploadMonth,
        year: uploadYear,
        entries: analyzedEntries
      });
      
      if (res.ok) {
        setShowUploadModal(false);
        setAnalyzedEntries([]);
        setUploadFile(null);
        // Set selected month/year to the uploaded one
        setSelectedMonth(uploadMonth.toString());
        setSelectedYear(uploadYear.toString());
        fetchGridData();
        alert(`Successfully uploaded ${analyzedEntries.length} entries!`);
      } else {
        throw new Error(res.data?.detail || "Failed to save entries");
      }
    } catch (error) {
      console.error("Error saving entries:", error);
      alert(`Error: ${error.message}`);
    } finally {
      setUploading(false);
    }
  };

  const deleteGridData = async (month, year) => {
    if (!window.confirm(`Delete all grid data for ${MONTH_NAMES[month - 1]} ${year}?`)) {
      return;
    }
    
    try {
      const res = await authDelete(`/api/advisor/health-grids/${month}/${year}`);
      if (res.ok) {
        if (selectedMonth === month.toString() && selectedYear === year.toString()) {
          setSelectedMonth("");
          setSelectedYear("");
        }
        fetchGridData();
      }
    } catch (error) {
      console.error("Error deleting grid:", error);
    }
  };

  const clearFilters = () => {
    setSelectedMonth("");
    setSelectedYear("");
    setSelectedCompany("");
    setSelectedPlan("");
    setSelectedSumInsured("");
    setSelectedFamily("");
    setSelectedLocation("");
  };

  // Get matching rate entry
  const matchingEntry = entries.length === 1 ? entries[0] : 
    (selectedCompany && selectedPlan && entries.length > 0 ? entries[0] : null);

  if (loading && entries.length === 0 && filters.available_months.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#FFFFFF] to-[#EDF1F8] flex items-center justify-center">
        <div className="text-center">
          <div className="h-12 w-12 border-4 border-cyan-400 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[#3A4557]">Loading commission grid...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FFFFFF] to-[#EDF1F8] pb-24 md:pb-0">
      {/* Mobile Sidebar */}
      <MobileSidebar isOpen={showSidebar} onClose={() => setShowSidebar(false)} />

      {/* Mobile Header */}
      <header className="md:hidden bg-white/95 backdrop-blur-xl sticky top-0 z-40 safe-area-inset-top border-b border-[#C9D4E6]">
        <div className="px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button 
              onClick={() => setShowSidebar(true)}
              className="p-2 -ml-2 rounded-xl bg-[#EDF1F8] hover:bg-[#E2E9F4] active:bg-[#E2E9F4] transition-colors"
            >
              <Menu className="h-5 w-5 text-[#1B2A4A]" />
            </button>
            <div className="h-10 w-10 rounded-xl bg-gradient-to-r from-cyan-400 to-cyan-500 flex items-center justify-center">
              <Grid3X3 className="h-5 w-5 text-[#0D1A3A]" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-[#1B2A4A]">Health Grid</h1>
              <p className="text-xs text-[#3A4557]">Commission Lookup</p>
            </div>
          </div>
          
          <button
            onClick={() => setShowUploadModal(true)}
            className="p-2.5 rounded-xl bg-gradient-to-r from-cyan-500 to-cyan-600 active:scale-95 transition-transform"
          >
            <Upload className="h-5 w-5 text-[#1B2A4A]" />
          </button>
        </div>
      </header>

      {/* Desktop Header */}
      <header className="hidden md:block bg-[#EDF1F8]/80 backdrop-blur-xl border-b border-[#C9D4E6] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate("/advisor/dashboard")}
                className="text-[#3A4557] hover:text-[#1B2A4A]"
                data-testid="back-btn"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
              <div className="h-8 w-px bg-[#E2E9F4]" />
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-xl bg-gradient-to-r from-cyan-400 to-cyan-500 flex items-center justify-center">
                  <Grid3X3 className="h-5 w-5 text-[#0D1A3A]" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-[#1B2A4A]">Health Grid</h1>
                  <p className="text-[#3A4557] text-sm">Commission Rate Lookup</p>
                </div>
              </div>
            </div>
            
            <Button
              onClick={() => setShowUploadModal(true)}
              className="bg-gradient-to-r from-cyan-500 to-cyan-600 text-white font-semibold rounded-full px-6"
              data-testid="upload-grid-btn"
            >
              <Upload className="h-4 w-4 mr-2" />
              Upload Grid
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        {/* Available Grids */}
        {filters.available_months.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 mb-6">
            <span className="text-[#3A4557] text-sm">Available grids:</span>
            {filters.available_months.map(({ month, year }) => (
              <div
                key={`${month}-${year}`}
                className={`flex items-center gap-2 border rounded-full px-3 py-1 cursor-pointer transition-all ${
                  selectedMonth === month.toString() && selectedYear === year.toString()
                    ? 'bg-cyan-500/20 border-cyan-500 text-cyan-600'
                    : 'bg-[#EDF1F8]/50 border-[#C9D4E6] text-[#1B2A4A] hover:border-cyan-500/50'
                }`}
                onClick={() => handleMonthYearChange(`${month}-${year}`)}
              >
                <Calendar className="h-3 w-3" />
                <span className="text-sm">{MONTH_NAMES[month - 1]} {year}</span>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    deleteGridData(month, year);
                  }}
                  className="text-[#3A4557] hover:text-red-600 transition-colors ml-1"
                  data-testid={`delete-grid-${month}-${year}`}
                >
                  <Trash2 className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* No Data State */}
        {filters.available_months.length === 0 ? (
          <Card className="bg-white/50 border-[#C9D4E6]">
            <CardContent className="py-16 text-center">
              <Grid3X3 className="h-16 w-16 text-[#3A4557] mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-[#1B2A4A] mb-2">No Grid Data</h3>
              <p className="text-[#3A4557] mb-6">
                Upload a commission rate PDF to get started
              </p>
              <Button
                onClick={() => setShowUploadModal(true)}
                className="bg-gradient-to-r from-cyan-500 to-cyan-600 text-white rounded-full"
              >
                <Upload className="h-4 w-4 mr-2" />
                Upload Grid
              </Button>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Rate Lookup Card */}
            <Card className="bg-white/50 border-[#C9D4E6] mb-6">
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-[#1B2A4A] flex items-center gap-2">
                    <Percent className="h-5 w-5 text-cyan-600" />
                    Commission Rate Lookup
                  </CardTitle>
                  {(selectedCompany || selectedPlan) && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={clearFilters}
                      className="text-[#3A4557] hover:text-[#1B2A4A]"
                    >
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Reset
                    </Button>
                  )}
                </div>
                <p className="text-[#3A4557] text-sm mt-1">
                  Select company and plan to view commission rates
                </p>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Step 1: Company */}
                <div className="space-y-2">
                  <label className="text-[#2E3B57] text-sm font-medium flex items-center gap-2">
                    <Building2 className="h-4 w-4 text-cyan-600" />
                    1. Select Company
                  </label>
                  <select
                    value={selectedCompany}
                    onChange={(e) => handleCompanyChange(e.target.value)}
                    className="w-full bg-white/70 border border-[#C9D4E6] rounded-xl px-4 py-3 text-[#1B2A4A] focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                    data-testid="select-company"
                  >
                    <option value="">-- Select Company --</option>
                    {filters.companies.map(c => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>

                {/* Step 2: Plan */}
                {selectedCompany && (
                  <div className="space-y-2 animate-fadeIn">
                    <label className="text-[#2E3B57] text-sm font-medium flex items-center gap-2">
                      <FileText className="h-4 w-4 text-cyan-600" />
                      2. Select Plan
                    </label>
                    <select
                      value={selectedPlan}
                      onChange={(e) => handlePlanChange(e.target.value)}
                      className="w-full bg-white/70 border border-[#C9D4E6] rounded-xl px-4 py-3 text-[#1B2A4A] focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                      data-testid="select-plan"
                    >
                      <option value="">-- Select Plan --</option>
                      {filters.plans.map(p => (
                        <option key={p} value={p}>{p}</option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Step 3: Sum Insured */}
                {selectedPlan && filters.sum_insureds.length > 1 && (
                  <div className="space-y-2 animate-fadeIn">
                    <label className="text-[#2E3B57] text-sm font-medium flex items-center gap-2">
                      <IndianRupee className="h-4 w-4 text-cyan-600" />
                      3. Select Sum Insured
                    </label>
                    <select
                      value={selectedSumInsured}
                      onChange={(e) => handleSumInsuredChange(e.target.value)}
                      className="w-full bg-white/70 border border-[#C9D4E6] rounded-xl px-4 py-3 text-[#1B2A4A] focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                      data-testid="select-sum-insured"
                    >
                      <option value="">-- Select Sum Insured --</option>
                      {filters.sum_insureds.map(si => (
                        <option key={si} value={si}>{si}</option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Step 4: Family Composition */}
                {selectedPlan && (selectedSumInsured || filters.sum_insureds.length <= 1) && filters.family_compositions.length > 1 && (
                  <div className="space-y-2 animate-fadeIn">
                    <label className="text-[#2E3B57] text-sm font-medium flex items-center gap-2">
                      <Users className="h-4 w-4 text-cyan-600" />
                      4. Select Family Composition
                    </label>
                    <select
                      value={selectedFamily}
                      onChange={(e) => handleFamilyChange(e.target.value)}
                      className="w-full bg-white/70 border border-[#C9D4E6] rounded-xl px-4 py-3 text-[#1B2A4A] focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                      data-testid="select-family"
                    >
                      <option value="">-- Select Family Type --</option>
                      {filters.family_compositions.map(f => (
                        <option key={f} value={f}>{f}</option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Step 5: Location */}
                {selectedPlan && filters.locations.length > 1 && (
                  <div className="space-y-2 animate-fadeIn">
                    <label className="text-[#2E3B57] text-sm font-medium flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-cyan-600" />
                      5. Select Location
                    </label>
                    <select
                      value={selectedLocation}
                      onChange={(e) => setSelectedLocation(e.target.value)}
                      className="w-full bg-white/70 border border-[#C9D4E6] rounded-xl px-4 py-3 text-[#1B2A4A] focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                      data-testid="select-location"
                    >
                      <option value="">-- Select Location --</option>
                      {filters.locations.map(l => (
                        <option key={l} value={l}>{l}</option>
                      ))}
                    </select>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Results Card */}
            {selectedCompany && selectedPlan && entries.length > 0 && (
              <Card className="bg-gradient-to-br from-[#FFFFFF]/80 to-[#EDF1F8]/50 border-cyan-500/30 overflow-hidden">
                <CardHeader className="bg-cyan-500/10 border-b border-cyan-500/20">
                  <CardTitle className="text-[#1B2A4A] flex items-center gap-2">
                    <CheckCircle2 className="h-5 w-5 text-cyan-600" />
                    Commission Rates
                  </CardTitle>
                  <p className="text-[#3A4557] text-sm">
                    {selectedCompany} - {selectedPlan}
                    {selectedSumInsured && ` - ${selectedSumInsured}`}
                    {selectedFamily && ` - ${selectedFamily}`}
                    {selectedLocation && ` - ${selectedLocation}`}
                  </p>
                </CardHeader>
                <CardContent className="p-6">
                  {entries.length === 1 ? (
                    // Single result - show prominently
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4 text-center">
                        <p className="text-green-600 text-sm mb-1">Fresh</p>
                        <p className="text-3xl font-bold text-[#1B2A4A]">
                          {entries[0].fresh_rate != null ? `${entries[0].fresh_rate}%` : '-'}
                        </p>
                      </div>
                      <div className="bg-purple-500/10 border border-purple-500/30 rounded-xl p-4 text-center">
                        <p className="text-purple-600 text-sm mb-1">Port</p>
                        <p className="text-3xl font-bold text-[#1B2A4A]">
                          {entries[0].port_rate != null ? `${entries[0].port_rate}%` : '-'}
                        </p>
                      </div>
                      <div className="bg-[#EDF1F8]/30 border border-blue-500/30 rounded-xl p-4 text-center">
                        <p className="text-[#2E3B57] text-sm mb-1">Renewal</p>
                        <p className="text-3xl font-bold text-[#1B2A4A]">
                          {entries[0].renewal_rate != null ? `${entries[0].renewal_rate}%` : '-'}
                        </p>
                      </div>
                      <div className="bg-[#D4A017]/10 border border-[#D4A017]/30 rounded-xl p-4 text-center">
                        <p className="text-[#A87608] text-sm mb-1">Renewal 60+</p>
                        <p className="text-3xl font-bold text-[#1B2A4A]">
                          {entries[0].renewal_senior_rate != null ? `${entries[0].renewal_senior_rate}%` : '-'}
                        </p>
                      </div>
                    </div>
                  ) : (
                    // Multiple results - show table
                    <Swipeable hint>
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-[#C9D4E6]">
                            <th className="text-left text-[#3A4557] text-sm py-2 px-2">Sum Insured</th>
                            <th className="text-left text-[#3A4557] text-sm py-2 px-2">Family</th>
                            <th className="text-left text-[#3A4557] text-sm py-2 px-2">Location</th>
                            <th className="text-center text-green-600 text-sm py-2 px-2">Fresh</th>
                            <th className="text-center text-purple-600 text-sm py-2 px-2">Port</th>
                            <th className="text-center text-[#2E3B57] text-sm py-2 px-2">Renewal</th>
                            <th className="text-center text-[#A87608] text-sm py-2 px-2">Renewal 60+</th>
                          </tr>
                        </thead>
                        <tbody>
                          {entries.slice(0, 20).map((entry, idx) => (
                            <tr key={idx} className="border-b border-[#C9D4E6]/50">
                              <td className="py-2 px-2 text-[#1B2A4A] text-sm">{entry.sum_insured}</td>
                              <td className="py-2 px-2 text-[#2E3B57] text-sm">{entry.family_composition}</td>
                              <td className="py-2 px-2 text-[#3A4557] text-sm">{entry.location}</td>
                              <td className="py-2 px-2 text-center">
                                <span className="text-green-600 font-semibold">
                                  {entry.fresh_rate != null ? `${entry.fresh_rate}%` : '-'}
                                </span>
                              </td>
                              <td className="py-2 px-2 text-center">
                                <span className="text-purple-600 font-semibold">
                                  {entry.port_rate != null ? `${entry.port_rate}%` : '-'}
                                </span>
                              </td>
                              <td className="py-2 px-2 text-center">
                                <span className="text-[#2E3B57] font-semibold">
                                  {entry.renewal_rate != null ? `${entry.renewal_rate}%` : '-'}
                                </span>
                              </td>
                              <td className="py-2 px-2 text-center">
                                <span className="text-[#A87608] font-semibold">
                                  {entry.renewal_senior_rate != null ? `${entry.renewal_senior_rate}%` : '-'}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {entries.length > 20 && (
                        <p className="text-[#3A4557] text-sm text-center mt-2">
                          Showing 20 of {entries.length} entries. Use filters to narrow down.
                        </p>
                      )}
                    </Swipeable>
                  )}
                </CardContent>
              </Card>
            )}

            {/* No results message */}
            {selectedCompany && selectedPlan && entries.length === 0 && !loading && (
              <Card className="bg-white/50 border-[#C9D4E6]">
                <CardContent className="py-8 text-center">
                  <p className="text-[#3A4557]">No matching rates found for the selected criteria.</p>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </main>

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <Card className="bg-white border-[#C9D4E6] w-full max-w-4xl max-h-[90vh] overflow-hidden">
            <CardHeader className="border-b border-[#C9D4E6]">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-xl bg-gradient-to-r from-cyan-400 to-cyan-500 flex items-center justify-center">
                    <Upload className="h-5 w-5 text-[#0D1A3A]" />
                  </div>
                  <div>
                    <CardTitle className="text-[#1B2A4A]">Upload Commission Grid</CardTitle>
                    <p className="text-[#3A4557] text-sm">AI will extract all rates from your PDF</p>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setShowUploadModal(false);
                    setAnalyzedEntries([]);
                    setUploadFile(null);
                  }}
                  className="text-[#3A4557] hover:text-[#1B2A4A]"
                >
                  <X className="h-5 w-5" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
              <div className="space-y-6">
                {/* Month/Year Selection */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-[#3A4557] text-sm mb-2 block">Month</label>
                    <select
                      value={uploadMonth}
                      onChange={(e) => setUploadMonth(parseInt(e.target.value))}
                      className="w-full bg-white/70 border border-[#C9D4E6] rounded-lg px-3 py-2 text-[#1B2A4A] focus:ring-2 focus:ring-cyan-500"
                      data-testid="upload-month"
                    >
                      {MONTH_NAMES.map((name, idx) => (
                        <option key={idx} value={idx + 1}>{name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-[#3A4557] text-sm mb-2 block">Year</label>
                    <select
                      value={uploadYear}
                      onChange={(e) => setUploadYear(parseInt(e.target.value))}
                      className="w-full bg-white/70 border border-[#C9D4E6] rounded-lg px-3 py-2 text-[#1B2A4A] focus:ring-2 focus:ring-cyan-500"
                      data-testid="upload-year"
                    >
                      {[2024, 2025, 2026, 2027].map(y => (
                        <option key={y} value={y}>{y}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* File Upload */}
                <div>
                  <label className="text-[#3A4557] text-sm mb-2 block">Upload PDF</label>
                  <div className="border-2 border-dashed border-[#C9D4E6] rounded-xl p-8 text-center hover:border-cyan-500/50 transition-colors">
                    <input
                      type="file"
                      accept=".pdf,.png,.jpg,.jpeg"
                      onChange={handleFileSelect}
                      className="hidden"
                      id="grid-file-input"
                      data-testid="file-input"
                    />
                    <label htmlFor="grid-file-input" className="cursor-pointer">
                      {uploadFile ? (
                        <div className="flex items-center justify-center gap-3">
                          <FileText className="h-8 w-8 text-cyan-600" />
                          <div className="text-left">
                            <p className="text-[#1B2A4A] font-medium">{uploadFile.name}</p>
                            <p className="text-[#3A4557] text-sm">{(uploadFile.size / 1024).toFixed(1)} KB</p>
                          </div>
                        </div>
                      ) : (
                        <>
                          <Upload className="h-12 w-12 text-[#3A4557] mx-auto mb-3" />
                          <p className="text-[#3A4557]">Click to upload PDF or Image</p>
                          <p className="text-[#3A4557] text-sm mt-1">Supported: PDF, PNG, JPG</p>
                        </>
                      )}
                    </label>
                  </div>
                </div>

                {/* Analyze Button */}
                {uploadFile && analyzedEntries.length === 0 && (
                  <Button
                    onClick={analyzePdf}
                    disabled={analyzing}
                    className="w-full bg-gradient-to-r from-cyan-500 to-cyan-600 text-white py-6"
                    data-testid="analyze-btn"
                  >
                    {analyzing ? (
                      <>
                        <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                        Analyzing PDF with AI (this may take 30-60 seconds)...
                      </>
                    ) : (
                      <>
                        <ArrowRight className="h-5 w-5 mr-2" />
                        Analyze PDF
                      </>
                    )}
                  </Button>
                )}

                {/* Preview Extracted Data */}
                {analyzedEntries.length > 0 && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="h-5 w-5 text-green-600" />
                        <span className="text-[#1B2A4A] font-medium">
                          Extracted {analyzedEntries.length} entries
                        </span>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setAnalyzedEntries([]);
                          setUploadFile(null);
                        }}
                        className="border-[#C9D4E6] text-[#3A4557]"
                      >
                        <RefreshCw className="h-4 w-4 mr-2" />
                        Re-upload
                      </Button>
                    </div>
                    
                    {/* Group by company for preview */}
                    <div className="bg-[#EDF1F8]/50 rounded-lg overflow-hidden max-h-64 overflow-y-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-[#EDF1F8] sticky top-0">
                          <tr>
                            <th className="text-left text-[#3A4557] px-3 py-2">Company</th>
                            <th className="text-left text-[#3A4557] px-3 py-2">Plan</th>
                            <th className="text-left text-[#3A4557] px-3 py-2">SI</th>
                            <th className="text-center text-green-600 px-3 py-2">Fresh</th>
                            <th className="text-center text-purple-600 px-3 py-2">Port</th>
                            <th className="text-center text-[#2E3B57] px-3 py-2">Renewal</th>
                            <th className="text-center text-[#A87608] px-3 py-2">60+</th>
                          </tr>
                        </thead>
                        <tbody>
                          {analyzedEntries.slice(0, 30).map((entry, idx) => (
                            <tr key={idx} className="border-t border-[#C9D4E6]/50">
                              <td className="px-3 py-2 text-[#1B2A4A]">{entry.company}</td>
                              <td className="px-3 py-2 text-[#2E3B57]">{entry.plan}</td>
                              <td className="px-3 py-2 text-[#3A4557]">{entry.sum_insured}</td>
                              <td className="px-3 py-2 text-center text-green-600">
                                {entry.fresh_rate != null ? `${entry.fresh_rate}%` : '-'}
                              </td>
                              <td className="px-3 py-2 text-center text-purple-600">
                                {entry.port_rate != null ? `${entry.port_rate}%` : '-'}
                              </td>
                              <td className="px-3 py-2 text-center text-[#2E3B57]">
                                {entry.renewal_rate != null ? `${entry.renewal_rate}%` : '-'}
                              </td>
                              <td className="px-3 py-2 text-center text-[#A87608]">
                                {entry.renewal_senior_rate != null ? `${entry.renewal_senior_rate}%` : '-'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {analyzedEntries.length > 30 && (
                        <p className="text-[#3A4557] text-center py-2 text-sm">
                          ... and {analyzedEntries.length - 30} more entries
                        </p>
                      )}
                    </div>

                    <Button
                      onClick={saveEntries}
                      disabled={uploading}
                      className="w-full bg-gradient-to-r from-green-500 to-green-600 text-white py-6"
                      data-testid="save-btn"
                    >
                      {uploading ? (
                        <>
                          <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <CheckCircle2 className="h-5 w-5 mr-2" />
                          Save {analyzedEntries.length} Entries for {MONTH_NAMES[uploadMonth - 1]} {uploadYear}
                        </>
                      )}
                    </Button>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <style jsx>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
      `}</style>

      {/* Mobile Bottom Navigation */}
      <MobileBottomNav onMenuClick={() => setShowSidebar(true)} />
    </div>
  );
}
